import { useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import {
  getFact,
  listAudit,
  listEveryFact,
  listFactKeys,
  listProductAudit,
  messageFor,
  type AuditEvent,
  type Fact,
  type FactKey,
} from "./api";
import { productsIn } from "./catalogue";
import { AuditEventSentence } from "./components/AuditEventSentence";
import { dayHeading, dayKey, humanTitle } from "./format";
import { useFactsLayout } from "./useFactsLayout";

const PAGE_SIZE = 50;

/**
 * What the rail has narrowed the trail to. A product and a fact are different
 * kinds of narrowing — the product reads every fact about it — so the choice
 * carries its kind rather than a bare key two lists could both own.
 */
export type AuditFilter = { kind: "product" | "fact"; key: string } | null;

/**
 * "Who changed this, when, from what, and against what source?"
 *
 * The tab renders APPROVAL EVENTS, not a summary of current facts — a fact list
 * has no column for an event time, a prior value, a source locator or the
 * self-approved badge, so the previous version could not answer its own
 * question. Events are read ACROSS facts (`GET /v1/audit`), so the rail on the
 * left is a FILTER: the feed renders with nothing selected, and choosing a
 * product or a fact narrows it. Products lead the rail, as the mock-up orders
 * it — "what changed on MCT Oil C8?" is the question a person arrives with
 * (KAL-410: the rail offered facts only, so there was no way to ask it).
 *
 * Every event is a SENTENCE. A row of columns (`approve · damien · {n:899}`)
 * makes the reader reassemble the sentence themselves, every time, and this tab
 * exists to be read by the people who never learn the schema.
 */
export function AuditTab({ areaName }: { areaName: string }) {
  // Two columns only when the TAB has room, measured like the Facts tab: at a
  // 1025px window with the AreaRail open, an `lg` split left the feed 210px
  // and its pager spilled past the page edge (AreaRail Option 1, KAL-355).
  const { ref: layoutRef, layout } = useFactsLayout<HTMLDivElement>();
  const split = layout !== "narrow";

  const [pageIndex, setPageIndex] = useState(0);
  const [filter, setFilter] = useState<AuditFilter>(null);
  const factKey = filter?.kind === "fact" ? filter.key : null;
  const productKey = filter?.kind === "product" ? filter.key : null;

  const keys = useQuery({
    queryKey: ["protex", "fact-keys"],
    queryFn: () => listFactKeys(),
  });

  // The product list, from the same shared catalogue read the Facts tab warms —
  // same key, same reader — so switching tabs costs no second fetch.
  const catalogue = useQuery({
    queryKey: ["protex", "facts", "all"],
    queryFn: () => listEveryFact({}, { limit: 200 }),
  });
  const products = productsIn(catalogue.data?.data.items ?? []);

  // Server-paged for everything and for one fact. NOT for a product: C2's feed
  // has no product filter, so that path is its own read (below).
  const feed = useQuery({
    queryKey: ["protex", "audit", "feed", factKey, pageIndex],
    queryFn: () =>
      listAudit({
        ...(factKey ? { fact_key: factKey } : {}),
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
      }),
    enabled: productKey === null,
  });

  // One product's whole trail, read once and paged here — the pager then moves
  // over rows already in hand instead of re-reading every fact per click.
  const productFeed = useQuery({
    queryKey: ["protex", "audit", "product", productKey],
    queryFn: () => listProductAudit(productKey as string),
    enabled: productKey !== null,
  });

  const events = productKey !== null ? productFeed : feed;
  const items =
    productKey !== null
      ? (productFeed.data?.items ?? []).slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE)
      : (feed.data?.data.items ?? []);
  const total =
    productKey !== null ? (productFeed.data?.items.length ?? 0) : (feed.data?.data.total ?? 0);
  const productTrailPartial = productKey !== null && productFeed.data?.complete === false;

  // The product a sentence names comes from the fact the event belongs to —
  // `AuditEventOut` carries the key but not the product. One query per DISTINCT
  // fact on the page, cached, so a fact with six events costs one request and a
  // page of events never fans out per row.
  const factIds = [...new Set(items.map((event) => event.fact_id))];
  const facts = useQueries({
    queries: factIds.map((id) => ({
      queryKey: ["protex", "fact", id],
      queryFn: () => getFact(id),
      // A sentence renders without the product rather than not at all, so a
      // failure here must not retry the whole page into a spin.
      retry: false,
    })),
  });
  const factById = new Map<string, Fact>();
  facts.forEach((result, index) => {
    if (result.data) factById.set(factIds[index], result.data.data);
  });

  const keyList = keys.data?.data ?? [];
  const days = groupByDay(items);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function choose(next: AuditFilter) {
    // Choosing the one already chosen is how the rail goes back to everything.
    setFilter(
      next !== null && filter?.kind === next.kind && filter.key === next.key ? null : next,
    );
    // A filter change makes the current page number meaningless.
    setPageIndex(0);
  }

  return (
    /* 300px is the mock-up's rail, and a FIXED track rather than a `minmax`
       one: an elastic rail took width from the feed at exactly the viewport
       where the sentences most need it, and the rail's content — a column of
       short fact names — never wanted the extra room. */
    <div
      ref={layoutRef}
      className={split ? "grid grid-cols-[18.75rem_minmax(0,1fr)]" : "grid grid-cols-1"}
    >
      <nav
        aria-label="Narrow the trail"
        className={`space-y-3 py-5 ${split ? "border-r border-ks-grey-4 pr-5" : ""}`}
      >
        <h2 className="text-xs font-bold tracking-[0.06em] text-ks-grey-1 uppercase">
          Narrow to one product or fact
        </h2>
        <div className="space-y-1">
        <FilterButton
          label="Everything"
          pressed={filter === null}
          onClick={() => choose(null)}
        />
        {catalogue.isLoading && <Skeleton className="h-12 w-full" />}
        {catalogue.isError && (
          <p role="alert" className="text-sm text-ks-danger">
            We could not read the product list, so the trail can only be narrowed by fact.{" "}
            {messageFor(catalogue.error, areaName)}
          </p>
        )}
        {products.map((product) => (
          <FilterButton
            key={`product:${product.product_key}`}
            label={product.product_name}
            // The mock-up's marker: a chosen product says it covers every fact
            // about it, so nobody reads it as one more fact name.
            suffix={productKey === product.product_key ? "all facts" : undefined}
            pressed={productKey === product.product_key}
            onClick={() => choose({ kind: "product", key: product.product_key })}
          />
        ))}
        {products.length > 0 && keyList.length > 0 && (
          <div role="separator" className="my-2 border-t border-ks-grey-4" />
        )}
        {keys.isLoading && <Skeleton className="h-24 w-full" />}
        {keys.isError && (
          <p role="alert" className="text-sm text-ks-danger">
            {messageFor(keys.error, areaName)}
          </p>
        )}
        {/* The filter list comes from the REGISTRY, not from a page of facts:
            every key is offered, including one whose only events are older than
            the page currently in view. */}
        {keyList.map((key) => (
          <FilterButton
            key={key.fact_key}
            label={humanTitle(key.fact_key, key)}
            pressed={factKey === key.fact_key}
            onClick={() => choose({ kind: "fact", key: key.fact_key })}
          />
        ))}
        </div>
        {/* Under the list, where the mock-up puts it: above the buttons it was
            read once and then permanently in the way of the thing it explains. */}
        <p className="text-xs leading-normal text-ks-grey-1">
          {filterNote(filter, products, keyList)}
        </p>
      </nav>

      <div className={`min-w-0 space-y-[18px] py-5 ${split ? "pl-6" : ""}`}>
        {events.isError ? (
          // An alert, never an empty feed: "no events" and "we could not load
          // events" are different claims.
          <Alert variant="destructive">
            <AlertTitle>We could not load the audit trail.</AlertTitle>
            <AlertDescription>
              {messageFor(events.error, areaName)}
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                // Retrying a READ changes nothing, so the read-only gate must
                // leave it live — a reader whose feed failed to load would
                // otherwise have no way to ask for it again.
                data-nonmutating
                onClick={() => void events.refetch()}
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : events.isLoading ? (
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-ks-ink">
            {/* An empty page is two different situations. With a filter on, the
                trail genuinely holds nothing for that key. PAST THE END it holds
                plenty — the reader has simply walked off it, and this branch
                rendered no pager at all, so with no filter set there was no
                control left on the screen to walk back. */}
            {pageIndex > 0 && total > 0 ? "No events on this page." : "No events yet."}{" "}
            {pageIndex > 0 && (
              <button
                type="button"
                data-nonmutating
                className="text-ks-purple underline underline-offset-2"
                onClick={() => setPageIndex(0)}
              >
                Back to the newest events
              </button>
            )}{" "}
            {filter !== null && (
              <button
                type="button"
                data-nonmutating
                className="text-ks-purple underline underline-offset-2"
                onClick={() => choose(null)}
              >
                Show everything
              </button>
            )}
          </p>
        ) : (
          <>
            {productTrailPartial && (
              <p className="text-[13px] text-ks-grey-1">
                This product has more facts than we could read in one go, so some older events
                may be missing from this list.
              </p>
            )}
            {days.map((day) => (
              <section key={day.key} className="pb-[18px]">
                {/* Real headings, so the feed is navigable by heading. */}
                <h3 className="text-xs font-bold tracking-[0.06em] text-ks-grey-1 uppercase">
                  {day.heading}
                </h3>
                <ul className="space-y-[18px] pt-[18px]">
                  {day.events.map((event) => (
                    <AuditEventSentence
                      key={event.event_id}
                      event={event}
                      fact={factById.get(event.fact_id)}
                      factKey={keyList.find((key) => key.fact_key === event.fact_key)}
                    />
                  ))}
                </ul>
              </section>
            ))}
            <nav
              aria-label="Pages"
              className="flex items-center gap-3 border-t border-ks-grey-4 pt-3 text-[13px] text-ks-grey-1"
            >
              {/* WHERE the reader is, not just how many rows are under them.
                  `{items.length} of {total}` printed "50 of 300 events" on
                  page 1, page 2 and page 6 alike — a pager whose only readout
                  is identical on every page cannot tell anyone which page they
                  are on, or that Older moved them at all. It leads the strip,
                  as the mock-up's count does; the paging sits at the far end. */}
              <span>{eventRangeLabel(pageIndex, items.length, total)}</span>
              <div className="grow" />
              <Button
                variant="ghost"
                size="sm"
                disabled={pageIndex <= 0}
                // Paging the trail is reading it. Without the marker the gate
                // pinned a reader to page one of the audit history — read-only
                // turned into read-the-first-fifty.
                data-nonmutating
                onClick={() => setPageIndex(pageIndex - 1)}
              >
                Newer
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={pageIndex >= pageCount - 1}
                data-nonmutating
                onClick={() => setPageIndex(pageIndex + 1)}
              >
                Older
              </Button>
            </nav>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * "Events 51-100 of 300" — the reader's POSITION, which is the one thing a
 * pager has to say.
 *
 * Built from the page index rather than from the server's echoed `offset` so
 * it cannot disagree with the buttons beside it: those move `pageIndex`, and a
 * range read off a response that has not arrived yet would lag them by one
 * click. A single-page trail gets the plain count — a range is noise when
 * there is nowhere to page to.
 */
export function eventRangeLabel(pageIndex: number, shown: number, total: number): string {
  if (shown === 0) return `0 of ${total} events`;
  if (shown >= total) return `${total} ${total === 1 ? "event" : "events"}`;
  const first = pageIndex * PAGE_SIZE + 1;
  return `Events ${first}\u2013${first + shown - 1} of ${total}`;
}

/**
 * One filter in the rail.
 *
 * The approved mock-up marks the chosen one with a 3px purple EDGE over a tint,
 * not with a full border: a ring around one item in a column of plain text rows
 * reads as a box that has appeared, and the eye spends a moment deciding whether
 * the others are missing theirs. The transparent edge is carried by every row so
 * choosing one moves nothing.
 *
 * The machine key is not printed here. It is the pane's disambiguator, where a
 * person has stopped to read one fact; in a rail scanned at a glance it doubled
 * the height of every row to restate the title in dot-case.
 */
function FilterButton({
  label,
  suffix,
  pressed,
  onClick,
}: {
  label: string;
  suffix?: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      // Reading an audit trail changes nothing — a read-only user is entitled to
      // every event, so the shell's edit lockout must not reach this control.
      data-nonmutating
      aria-pressed={pressed}
      onClick={onClick}
      className={[
        "block min-h-11 w-full rounded-lg border-l-[3px] py-2.5 pr-3 pl-[9px] text-left text-sm",
        pressed
          ? "border-ks-purple bg-ks-purple-4 font-bold text-ks-ink"
          : "border-transparent text-ks-ink hover:bg-ks-grey-4/50",
      ].join(" ")}
    >
      {label}
      {suffix && (
        <span className="text-[13px] font-normal text-ks-grey-1"> · {suffix}</span>
      )}
    </button>
  );
}

/**
 * The line under the rail: what the feed is showing right now, by NAME.
 *
 * Live read "Showing everything" whatever was pressed (KAL-410), so the rail
 * never confirmed that a click had narrowed anything.
 */
export function filterNote(
  filter: AuditFilter,
  products: Array<{ product_key: string; product_name: string }>,
  keys: FactKey[],
): string {
  if (filter === null) {
    return "Showing everything. Pick a product or a fact to narrow the trail; click it again to go back.";
  }
  if (filter.kind === "product") {
    const name = products.find((p) => p.product_key === filter.key)?.product_name ?? "one product";
    return `Showing every fact on ${name}. Click it again to go back to everything.`;
  }
  const key = keys.find((k) => k.fact_key === filter.key);
  return `Showing ${humanTitle(filter.key, key)}. Click it again to go back to everything.`;
}

/**
 * Events grouped under the day they happened.
 *
 * The feed arrives newest-first and contiguous, so grouping the page is a
 * display decision over rows already in hand — not a claim that the page is the
 * whole trail. The pager says how many events there are in total.
 */
export function groupByDay(
  events: AuditEvent[],
): Array<{ key: string; heading: string; events: AuditEvent[] }> {
  const days: Array<{ key: string; heading: string; events: AuditEvent[] }> = [];
  for (const event of events) {
    const key = dayKey(event.created_at);
    const last = days[days.length - 1];
    if (last && last.key === key) last.events.push(event);
    else days.push({ key, heading: dayHeading(event.created_at), events: [event] });
  }
  return days;
}
